/**
 * Module: lib/copilot/useCopilot
 * Consumer hook for the CopilotProvider. Throws if used outside.
 *
 * Selector-based (use-context-selector), matching the AutomationProvider
 * pattern: consumers subscribe to a narrow slice so streaming-token updates
 * to `chat` don't re-render components that only need e.g. `widgetOpen`.
 */

import { useContextSelector } from 'use-context-selector'
import { CopilotContext, type CopilotContextValue } from './CopilotProvider'

export function useCopilot<Selected>(selector: (value: CopilotContextValue) => Selected): Selected {
  return useContextSelector(CopilotContext, ctx => {
    if (!ctx) {
      throw new Error('useCopilot must be used inside <CopilotProvider>')
    }
    return selector(ctx)
  })
}

/**
 * Null-safe variant for chrome that renders whether or not the provider is
 * mounted (e.g. PrimaryTabs — CopilotShell mounts the provider only for
 * TAB_COPILOT users). Returns `fallback` when there is no provider.
 */
export function useCopilotOptional<Selected>(
  selector: (value: CopilotContextValue) => Selected,
  fallback: Selected
): Selected {
  return useContextSelector(CopilotContext, ctx => (ctx ? selector(ctx) : fallback))
}
